import { useEffect, useRef, useState } from "react";

/**
 * Debounce `value` with an immediate leading edge.
 *
 * A change arriving `delayMs` or more after the previous change applies
 * immediately — an isolated change never waits. A change arriving within
 * `delayMs` of the previous change is deferred by `delayMs` from its own
 * arrival. The window restarts on every change whether or not that change
 * itself applied immediately, so a rapid sequence surfaces only its first and
 * final values.
 */
const useDebouncedValue = <T>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);
  const lastValueRef = useRef(value);
  const lastChangeAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (Object.is(lastValueRef.current, value)) {
      return;
    }
    lastValueRef.current = value;

    const now = Date.now();
    const previousChangeAt = lastChangeAtRef.current;
    lastChangeAtRef.current = now;

    if (previousChangeAt === null || now - previousChangeAt >= delayMs) {
      setDebounced(value);
      return;
    }
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
};

export default useDebouncedValue;
